/**
 * TemplateInfoWidget — Template name, description, and status badge.
 *
 * Experience widget for Item Customization Template detail page.
 * Reads/writes via StandardDetailPage's formData + onFieldChange.
 *
 * Plan: 126-template-detail-page, Epic 01
 */

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { TemplateStatusBadge } from "../components/TemplateStatusBadge";
import { useValidateForActivation } from "../api/customizationAdminHooks";
import type { ActivationError } from "../api/customizationAdminTypes";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";

const i18n = {
  templateName: { en: "Template Name", th: "ชื่อแม่แบบ" },
  description: { en: "Description", th: "คำอธิบาย" },
  status: { en: "Status", th: "สถานะ" },
  placeholder_name: { en: "e.g., Embroidery Options", th: "เช่น ตัวเลือกปักชื่อ" },
  placeholder_desc: { en: "Describe what this template is for...", th: "อธิบายว่าแม่แบบนี้ใช้สำหรับอะไร..." },
  validating: { en: "Validating...", th: "กำลังตรวจสอบ..." },
  save_before_activate: { en: "Save the template before activating", th: "บันทึกแม่แบบก่อนเปิดใช้งาน" },
  save_changes_first: { en: "Save changes before activating", th: "บันทึกการเปลี่ยนแปลงก่อนเปิดใช้งาน" },
  cannot_activate: { en: "Cannot activate — fix these issues:", th: "ไม่สามารถเปิดใช้งาน — แก้ไขปัญหาเหล่านี้:" },
} as const;

export function TemplateInfoWidget({
  formData,
  onFieldChange,
  disabled,
  isSaving,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const t = (key: keyof typeof i18n) => i18n[key][language] || i18n[key].en;

  const templateName = (formData.template_name as string) || "";
  const description = (formData.description as string) || "";
  const status = (formData.status as "Draft" | "Active") || "Draft";
  const docName = formData.name as string | undefined;
  const isNew = !docName;

  // Auto-focus template name on new documents
  const nameInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isNew && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isNew]);

  // Activation validation
  const validateMutation = useValidateForActivation();
  const [activationErrors, setActivationErrors] = useState<ActivationError[]>([]);

  const handleStatusClick = () => {
    if (disabled || isSaving) return;

    if (status === "Active") {
      setActivationErrors([]);
      onFieldChange("status", "Draft");
      return;
    }

    if (isNew) {
      toast.warning(t("save_before_activate"));
      return;
    }

    if (formData.__unsaved) {
      toast.warning(t("save_changes_first"));
      return;
    }

    validateMutation.mutate(docName!, {
      onSuccess: (data) => {
        if (data.valid) {
          setActivationErrors([]);
          onFieldChange("status", "Active");
        } else {
          setActivationErrors(data.errors || []);
        }
      },
    });
  };

  return (
    <div>
      <SectionHeader label={getSectionTitleTranslation("Template Info", language)} icon={Package} color="blue" />
      <div className="rounded-2xl overflow-hidden bg-card shadow-apple p-6 space-y-5">
        {/* Template Name */}
        <div className="space-y-1.5">
          <Label htmlFor="template-name" className="text-sm font-medium">
            {t("templateName")}
          </Label>
          <Input
            ref={nameInputRef}
            id="template-name"
            value={templateName}
            onChange={(e) => onFieldChange("template_name", e.target.value)}
            placeholder={t("placeholder_name")}
            disabled={disabled || isSaving}
          />
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label htmlFor="template-description" className="text-sm font-medium">
            {t("description")}
          </Label>
          <Textarea
            id="template-description"
            value={description}
            onChange={(e) => onFieldChange("description", e.target.value)}
            placeholder={t("placeholder_desc")}
            rows={2}
            disabled={disabled || isSaving}
          />
        </div>

        {/* Status Badge */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">{t("status")}</Label>
          <div>
            <button
              type="button"
              onClick={handleStatusClick}
              disabled={disabled || isSaving || validateMutation.isPending}
              className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 transition-opacity"
              aria-label={`${t("status")}: ${status}`}
            >
              <TemplateStatusBadge status={status} />
            </button>
            {validateMutation.isPending && (
              <span className="ml-2 text-xs text-muted-foreground">
                {t("validating")}
              </span>
            )}
          </div>

          {/* Activation errors */}
          {activationErrors.length > 0 && (
            <div className="mt-2 rounded-sm border border-destructive/30 bg-destructive/5 p-3">
              <div className="flex items-center gap-1.5 text-sm font-medium text-destructive mb-1.5">
                <AlertCircle className="h-4 w-4" />
                {t("cannot_activate")}
              </div>
              <ul className="list-disc list-inside text-sm text-destructive/80 space-y-0.5">
                {activationErrors.map((err, i) => (
                  <li key={i}>
                    {err.field_label}: {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
